'use client';

import React, { useState } from 'react';
import { Check, X, ArrowRight, Sparkles, Building2 } from 'lucide-react';
import { Switch } from "@/components/ui/Switch";
import { Button } from "@/components/ui/Button";
import Link from 'next/link';
import { cn, getFeatureDescription, getFeatureName } from "@/lib/utils/helpers";
import { PLAN_CONFIG } from "@/lib/utils/helpers";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { usePageView } from '@/lib/hooks/usePageView';

interface PriceCardProps {
  name: keyof typeof PLAN_CONFIG;
  yearlyBilling: boolean;
}

const PriceCard = ({ name, yearlyBilling }: PriceCardProps) => {
  const plan = PLAN_CONFIG[name];
  const price = yearlyBilling ? plan.prices.yearly : plan.prices.monthly;
  
  const FeatureCheck = ({ available }: { available: boolean }) => (
    available ? 
      <Check className="h-5 w-5 text-emerald-500 mr-3 flex-shrink-0" /> : 
      <X className="h-5 w-5 text-slate-600 mr-3 flex-shrink-0" />
  );

  type FeatureType = 
    | "organizations"
    | "projects"
    | "users"
    | "totalParticipants"
    | "scenariosPerProject"
    | "customQuestions"
    | "emailSupport"
    | "advancedAnalytics"
    | "customBranding"
    | "prioritySupport"
    | "dedicatedAccountManager"
    | "customIntegration";

  interface FeatureItemProps {
    feature: keyof typeof PLAN_CONFIG['Free']['limits'];
    value: number | boolean | string;
    available: boolean;
  }

  const FeatureItem = ({ feature, value, available }: FeatureItemProps) => {
    const featureName = getFeatureName(feature as FeatureType);

    return (
      <li className="flex items-start group py-2">
        <FeatureCheck available={available} />
        <span className={cn(
          "text-sm flex items-center gap-2",
          available ? "text-slate-300" : "text-slate-600"
        )}>
          {typeof value === 'number' ? (
            `${value} ${featureName}`
          ) : (
            featureName
          )}
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <span className="text-slate-600 cursor-help opacity-0 group-hover:opacity-100 transition-opacity">ⓘ</span>
              </TooltipTrigger>
              <TooltipContent className="bg-slate-800 border-slate-700">
                <p className="w-[200px] text-sm text-slate-300">{getFeatureDescription(feature)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
      </li>
    );
  };

  return (
    <div 
      className={cn(
        "relative rounded-2xl p-6 lg:p-8 transition-all duration-300",
        plan.popular 
          ? "bg-gradient-to-b from-amber-700/10 to-amber-800/5 border-2 border-amber-600/50 shadow-xl shadow-amber-900/10" 
          : "bg-slate-900/50 border border-white/5 hover:border-white/10"
      )}
    >
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-600 to-amber-700 text-white text-sm font-medium shadow-lg">
            <Sparkles className="w-4 h-4" />
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">{name}</h3>
        <p className="text-slate-400 text-sm">{plan.description}</p>
      </div>

      <div className="text-center mb-6">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-4xl lg:text-5xl font-bold text-white">${price.toLocaleString('en-US')}</span>
          <span className="text-slate-400">/{yearlyBilling ? 'year' : 'month'}</span>
        </div>
        {yearlyBilling && Number(plan.prices.monthly) > 0 && (
          <p className="mt-2 text-sm text-emerald-400">
            Save ${((plan.prices.monthly * 12) - plan.prices.yearly).toFixed(0)} yearly
          </p>
        )}
      </div>

      <ul className="space-y-1 mb-8 border-t border-white/5 pt-6">
        {Object.entries(plan.limits).map(([feature, value]) => {
          // Skip organizations - everyone gets 1 org, not a differentiating feature
          if (feature === 'organizations') return null;
          if (value === undefined || value === null) return null;
          
          return (
            <FeatureItem
              key={feature}
              feature={feature as keyof typeof PLAN_CONFIG['Free']['limits']}
              value={value}
              available={!!value}
            />
          );
        })}
      </ul>

      <Link href="/auth/signup">
        <Button 
          className={cn(
            "w-full py-6 rounded-xl font-semibold transition-all duration-300",
            plan.popular 
              ? "bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white shadow-lg shadow-amber-900/25 hover:shadow-amber-900/40" 
              : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
          )}
        >
          {price === 0 ? 'Start Free' : 'Get Started'}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </Link>
    </div>
  );
};

export default function Pricing() {
  usePageView();
  const [yearlyBilling, setYearlyBilling] = useState(false);

  return (
    <div className="container mx-auto px-4 py-16 lg:py-24">
      {/* Header */}
      <div className="text-center mb-16 max-w-3xl mx-auto">
        <p className="text-amber-500 font-semibold text-sm uppercase tracking-wider mb-3">
          Pricing
        </p>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
          Simple, Transparent 
          <span className="bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent"> Pricing</span>
        </h1>
        <p className="text-lg text-slate-400">
          Start free, upgrade when you need more. No hidden fees, no surprises.
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center items-center gap-4 mb-12">
        <span className={cn(
          "text-sm font-medium transition-colors",
          !yearlyBilling ? "text-white" : "text-slate-500"
        )}>
          Monthly
        </span>
        <Switch 
          checked={yearlyBilling}
          onCheckedChange={setYearlyBilling}
          className="data-[state=checked]:bg-amber-600"
        />
        <span className={cn(
          "text-sm font-medium transition-colors",
          yearlyBilling ? "text-white" : "text-slate-500"
        )}>
          Yearly
          <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-medium">
            Save 20%
          </span>
        </span>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
        {(Object.keys(PLAN_CONFIG) as Array<keyof typeof PLAN_CONFIG>).map((planName) => (
          <PriceCard
            key={planName as string}
            name={planName}
            yearlyBilling={yearlyBilling}
          />
        ))}
      </div>

      {/* Enterprise Section */}
      <div className="mt-20 max-w-4xl mx-auto">
        <div className="relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 to-slate-800" />
          <div className="relative p-8 lg:p-12 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl font-bold text-white mb-2">Need a Custom Solution?</h2>
              <p className="text-slate-400">
                Get dedicated support, custom integrations, SSO, and flexible pricing for large organizations.
              </p>
            </div>
            <Link href="/contact">
              <Button 
                variant="outline" 
                size="lg"
                className="bg-white/5 border-white/10 hover:bg-white/10 text-white px-8"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* FAQ Preview */}
      <div className="mt-16 text-center">
        <p className="text-slate-400 mb-4">
          Have questions about our pricing?
        </p>
        <Link
          href="/faq"
          className="text-amber-500 hover:text-amber-400 font-medium inline-flex items-center gap-2"
        >
          View FAQs
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
